import type { AlertColor }                                 from "@mui/material";
import { Alert, Snackbar }                                 from "@mui/material";
import type React                                          from "react";
import type { ReactNode }                                  from "react";
import { createContext, useCallback, useMemo, useReducer } from "react";

export const NotificationActionTypeEnum = {
  SHOW: "SHOW",
  HIDE: "HIDE",
} as const;

export type NotificationActionType = typeof NotificationActionTypeEnum[keyof typeof NotificationActionTypeEnum];

type NotificationState = {
  open             : boolean;
  message          : ReactNode;
  severity         : AlertColor;
  autoHideDuration?: number;
}

type NotificationContextType = {
  showNotification: (_message: ReactNode, _autoHideDuration?: number, _severity?: AlertColor) => void;
  showSuccess     : (_message: ReactNode, _autoHideDuration?: number) => void;
  showError       : (_message: ReactNode, _autoHideDuration?: number) => void;
  showWarning     : (_message: ReactNode, _autoHideDuration?: number) => void;
  showInfo        : (_message: ReactNode, _autoHideDuration?: number) => void;
  hideNotification: () => void;
}

type NotificationAction = 
  | { type: typeof NotificationActionTypeEnum.SHOW; payload: { message: ReactNode; severity: AlertColor; autoHideDuration?: number } }
  | { type: typeof NotificationActionTypeEnum.HIDE };

const initialState: NotificationState = {
  open            : false,
  message         : "",
  severity        : "info",
  autoHideDuration: 6000,
};

const notificationReducer = (state: NotificationState, action: NotificationAction): NotificationState => {
  switch (action.type) {
    case NotificationActionTypeEnum.SHOW:
      return {
        ...state,
        open            : true,
        message         : action.payload.message,
        severity        : action.payload.severity,
        autoHideDuration: action.payload.autoHideDuration || 6000,
      };
    case NotificationActionTypeEnum.HIDE:
      return {
        ...state,
        open: false,
      };
    default:
      return state;
  }
};

export const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(notificationReducer, initialState);

  const showNotification = useCallback((message: ReactNode, autoHideDuration: number = 6000, severity: AlertColor = "info") => {
    dispatch({
 type   : NotificationActionTypeEnum.SHOW,
payload: {
 message, severity, autoHideDuration 
} 
});
  }, []);

  const showSuccess = useCallback((message: ReactNode, autoHideDuration: number = 6000) => {
    showNotification(message, autoHideDuration, "success");
  }, [showNotification]);

  const showError = useCallback((message: ReactNode, autoHideDuration: number = 6000) => {
    showNotification(message, autoHideDuration, "error");
  }, [showNotification]);

  const showWarning = useCallback((message: ReactNode, autoHideDuration: number = 6000) => {
    showNotification(message, autoHideDuration, "warning");
  }, [showNotification]);

  const showInfo = useCallback((message: ReactNode, autoHideDuration: number = 6000) => {
    showNotification(message, autoHideDuration, "info");
  }, [showNotification]);

  const hideNotification = useCallback(() => {
    dispatch({ type: NotificationActionTypeEnum.HIDE });
  }, []);

  const value: NotificationContextType = useMemo(() => ({
    showNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    hideNotification,
  }), [showNotification, showSuccess, showError, showWarning, showInfo, hideNotification]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <Snackbar
        open={state.open}
        autoHideDuration={state.autoHideDuration}
        onClose={hideNotification}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={hideNotification}
          severity={state.severity}
          sx={{
 width: "100%", textAlign: "center", display: "flex", justifyContent: "center", alignItems: "center" 
}}
        >
          {state.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
};
